import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Box,
  Fade,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { useSnackbar } from "notistack";
import { OutlineMove } from "../../../../../Icon";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { updateInterview } from "../../../../../../apis";
import { useTranslation } from "react-i18next";
import { UnsavedChanges } from "../../../../../Modal/Confirmation/UnsavedChanges/UnsavedChanges";
import useUnsavedChangesWarning from "../../../../../../hooks/useUnsavedChangesWarning";

export const EditQnA = forwardRef(
  (
    {
      alias,
      id,
      isLoading,
      setIsLoading,
      goHome,
      questionnaire = [],
      interviewed,
      isMobil,
      setInterviews,
    },
    ref
  ) => {
    const theme = useTheme();
    const inputRefs = useRef({});
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [t] = useTranslation("investigation");

    // -----> Initial States <----- //
    const [openCancelDialog, setOpenCancelDialog] = useState(false);

    const [questions, setQuestions] = useState(
      questionnaire.length > 0
        ? questionnaire.map((q) => ({ ...q, showDetails: !!q.details }))
        : [{ question: "", details: "", answer: "", showDetails: false }]
    );
    const [activeIndex, setActiveIndex] = useState(
      questionnaire.length === 0 ||
        (questionnaire.length === 1 &&
          !(questionnaire[0].question || "").trim())
        ? 0
        : null
    );

    // Reset questions to their original state
    const resetQuestions = useCallback(() => {
      setQuestions(
        questionnaire.length > 0
          ? questionnaire.map((q) => ({ ...q, showDetails: !!q.details }))
          : [{ question: "", details: "", answer: "", showDetails: false }]
      );
      setActiveIndex(
        questionnaire.length === 0 ||
          (questionnaire.length === 1 &&
            !(questionnaire[0].question || "").trim())
          ? 0
          : null
      );
    }, [questionnaire]);

    // When the questionnaire changes, resets status.
    useEffect(() => {
      resetQuestions();
    }, [questionnaire, resetQuestions]);

    // --- Memo to detect any empty question (question text missing) ---
    const hasEmptyQuestions = useMemo(
      () => questions.some((q) => (q.question || "").trim() === ""),
      [questions]
    );

    // Focus the corresponding input
    useEffect(() => {
      if (activeIndex !== null) {
        setTimeout(() => {
          const input = inputRefs.current[`q-${activeIndex}`];
          if (input) {
            input.focus();
            const val = input.value || "";
            try {
              input.setSelectionRange(val.length, val.length);
            } catch (e) {
              console.error(e);
            }
          }
        }, 0);
      }
    }, [activeIndex]);

    // Insert a new question just below the current index
    const insertQuestionAt = useCallback(
      (idx) => {
        if (questions.some((q) => (q.question || "").trim() === "")) {
          enqueueSnackbar(
            t("Interview.complete_empty_question_before_adding_new_one"),
            {
              variant: "info",
              preventDuplicate: true,
              anchorOrigin: { vertical: "top", horizontal: "center" },
            }
          );
          const firstEmpty = questions.findIndex(
            (q) => (q.question || "").trim() === ""
          );
          if (firstEmpty >= 0) {
            setActiveIndex(firstEmpty);
            setTimeout(() => inputRefs.current[`q-${firstEmpty}`]?.focus(), 0);
          }
          return;
        }

        setQuestions((prev) => {
          const updated = [...prev];
          updated.splice(idx + 1, 0, {
            question: "",
            details: "",
            answer: "",
            showDetails: false,
          });
          return updated;
        });
        setActiveIndex(idx + 1);
        setTimeout(() => inputRefs.current[`q-${idx + 1}`]?.focus(), 0);
      },
      [questions, enqueueSnackbar, t]
    );

    // Duplicate item
    const duplicateQuestionAt = useCallback(
      (idx) => {
        if (questions.some((q) => (q.question || "").trim() === "")) {
          enqueueSnackbar(
            t("Interview.complete_empty_question_before_adding_new_one"),
            {
              variant: "info",
              preventDuplicate: true,
              anchorOrigin: { vertical: "top", horizontal: "center" },
            }
          );
          const firstEmpty = questions.findIndex(
            (q) => (q.question || "").trim() === ""
          );
          if (firstEmpty >= 0) {
            setActiveIndex(firstEmpty);
            setTimeout(() => inputRefs.current[`q-${firstEmpty}`]?.focus(), 0);
          }
          return;
        }

        setQuestions((prev) => {
          const updated = [...prev];
          if (idx < 0 || idx >= updated.length) return updated;

          const toCopy = updated[idx];
          const copy = {
            question: toCopy.question || "",
            details: toCopy.details || "",
            answer: toCopy.answer || "",
            showDetails: !!toCopy.showDetails,
          };

          updated.splice(idx + 1, 0, copy);
          return updated;
        });

        setActiveIndex(idx + 1);
        setTimeout(() => inputRefs.current[`q-${idx + 1}`]?.focus(), 0);
      },
      [questions, enqueueSnackbar, t]
    );

    // Delete item
    const removeQuestion = useCallback(
      (idx) => {
        setQuestions((prev) => {
          const deleted = prev[idx];
          const updated = prev.filter((_, i) => i !== idx);

          if (updated.length === 0) {
            const emptyQuestion = {
              question: "",
              details: "",
              answer: "",
              showDetails: false,
            };
            enqueueSnackbar(t("Interview.last_question_deleted"), {
              variant: "warning",
              preventDuplicate: true,
              ...(isMobil
                ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
                : {}),
              action: (key) => (
                <EGButton
                  label={t("Interview.undo")}
                  color="warning"
                  variant="outlined"
                  borderColor="white"
                  onClick={() => {
                    setQuestions([deleted]);
                    setActiveIndex(0);
                    closeSnackbar(key);
                    setTimeout(() => inputRefs.current[`q-0`]?.focus(), 0);
                  }}
                />
              ),
            });
            setActiveIndex(0);
            setTimeout(() => inputRefs.current[`q-0`]?.focus(), 0);
            return [emptyQuestion];
          } else {
            const newIndex = idx > 0 ? idx - 1 : 0;
            setActiveIndex(newIndex);
            enqueueSnackbar(t("Interview.question_deleted"), {
              variant: "warning",
              preventDuplicate: true,
              ...(isMobil
                ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
                : {}),
              action: (key) => (
                <EGButton
                  color="warning"
                  variant="outlined"
                  borderColor="white"
                  label={t("Interview.undo")}
                  onClick={() => {
                    setQuestions((cur) => {
                      const restored = [...cur];
                      restored.splice(idx, 0, deleted);
                      return restored;
                    });
                    setActiveIndex(idx);
                    closeSnackbar(key);
                    setTimeout(() => inputRefs.current[`q-${idx}`]?.focus(), 0);
                  }}
                />
              ),
            });
            return updated;
          }
        });
      },
      [enqueueSnackbar, closeSnackbar, t]
    );

    // Question OnChange event
    const hdlChangeQuestion = useCallback((idx, field, value) => {
      setQuestions((prev) => {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], [field]: value };
        return updated;
      });
    }, []);

    // Show/hide observations
    const toggleObservaciones = useCallback(
      (idx) => {
        setQuestions((prev) => {
          const updated = [...prev];
          if (updated[idx].showDetails && updated[idx].details.trim() !== "") {
            const prevDetails = updated[idx].details;
            updated[idx] = { ...updated[idx], showDetails: false, details: "" };
            enqueueSnackbar(
              t("Interview.the_question_objective_has_been_removed"),
              {
                variant: "info",
                preventDuplicate: true,
                ...(isMobil
                  ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
                  : {}),
                action: (key) => (
                  <EGButton
                    label={t("Interview.undo")}
                    color="info"
                    variant="outlined"
                    borderColor="white"
                    size="small"
                    onClick={() => {
                      setQuestions((cur2) => {
                        const restored = [...cur2];
                        restored[idx] = {
                          ...restored[idx],
                          showDetails: true,
                          details: prevDetails,
                        };
                        return restored;
                      });
                      setActiveIndex(idx);
                      closeSnackbar(key);

                      // 👇 Esperamos un frame para asegurar el render del TextField
                      setTimeout(() => {
                        const input = inputRefs.current[`d-${idx}`];
                        if (input) input.focus();
                      }, 50);
                    }}
                  />
                ),
              }
            );
          } else {
            updated[idx] = {
              ...updated[idx],
              showDetails: !updated[idx].showDetails,
              details: updated[idx].showDetails ? "" : updated[idx].details,
            };
          }
          return updated;
        });

        setActiveIndex(idx);

        // 👇 Si estamos activando el campo de observaciones, enfocar su input
        setTimeout(() => {
          const input = inputRefs.current[`d-${idx}`];
          if (input) input.focus();
        }, 50);
      },
      [enqueueSnackbar, closeSnackbar, t, isMobil]
    );

    // Auxiliary function for formatting the index list
    const formatIndexes = (
      indexes,
      conjunction = "and",
      useOxfordComma = false
    ) => {
      const withHashes = indexes.map((i) => `#${i + 1}`);

      if (withHashes.length === 1) return withHashes[0];
      if (withHashes.length === 2) return withHashes.join(` ${conjunction} `);

      //  For lists of 3 or more items
      const last = withHashes.pop();
      const comma = useOxfordComma ? "," : "";
      return `${withHashes.join(", ")}${comma} ${conjunction} ${last}`;
    };

    const hasUnsavedChanges = useMemo(() => {
      const initial = questionnaire.map(({ question, details, answer }) => ({
        question: (question || "").trim(),
        details: (details || "").trim(),
        answer: (answer || "").trim(),
      }));

      const current = questions.map(({ question, details, answer }) => ({
        question: (question || "").trim(),
        details: (details || "").trim(),
        answer: (answer || "").trim(),
      }));

      return JSON.stringify(initial) !== JSON.stringify(current);
    }, [questions, questionnaire]);

    // Hook to prevent exiting without saving in the browser
    useUnsavedChangesWarning(!!hasUnsavedChanges);

    // Cancel
    const handleCancel = useCallback(() => {
      if (hasUnsavedChanges) {
        setOpenCancelDialog(true);
      } else {
        goHome();
      }
    }, [hasUnsavedChanges, goHome]);

    // Save
    const handleSave = useCallback(async () => {
      const invalidIndexes = questions
        .map((q, idx) =>
          q.question.trim() === "" &&
          (q.details.trim() !== "" || q.answer.trim() !== "")
            ? idx
            : null
        )
        .filter((i) => i !== null);

      if (invalidIndexes.length > 0) {
        const msg =
          invalidIndexes.length === 1
            ? `${t("Interview.the_question")} #${invalidIndexes[0] + 1} ${t(
                "Interview.is_incomplete_no_question_text"
              )}`
            : `${t("Interview.the_questions")} ${formatIndexes(
                invalidIndexes,
                t("Interview.and"),
                true
              )} ${t("Interview.are_incomplete_no_question")}`;

        enqueueSnackbar(msg, {
          variant: "warning",
          ...(isMobil
            ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
            : {}),
        });
        return;
      }

      const interview = questions
        .filter((q) => q.question.trim() !== "")
        .map(({ question, details }) => ({
          question: question.trim(),
          details: details.trim(),
        }));

      if (interview.length === 0) {
        enqueueSnackbar(
          t("Interview.the_interview_is_empty_add_questions_before_saving"),
          {
            variant: "error",
            ...(isMobil
              ? { anchorOrigin: { vertical: "top", horizontal: "center" } }
              : {}),
          }
        );
        return;
      }
      setIsLoading(true);
      const saving = enqueueSnackbar(`${t("Interview.saving")}...`, {
        variant: "info",
        persist: true,
      });
      try {
        const dataToSend = {
          alias,
          interviews_id: id,
          interviews_data: { interview },
        };
        const resp = await updateInterview(dataToSend);
        setInterviews(resp?.data?.interviews);
        goHome();
        enqueueSnackbar(t("Interview.data_saved_successfully"), {
          variant: "success",
        });
      } catch (error) {
        console.error("Error al guardar:", error);
      } finally {
        setIsLoading(false);
        closeSnackbar(saving);
      }
    }, [questions, enqueueSnackbar, goHome, alias, id, updateInterview]);

    // Expose methods to parent
    useImperativeHandle(
      ref,
      () => ({
        handleCancel,
        handleSave,
      }),
      [handleCancel, handleSave]
    );

    // Helpers to activate and focus based on where the user clicks
    const activateAndFocusQuestion = (index) => {
      setActiveIndex(index);
      setTimeout(() => inputRefs.current[`q-${index}`]?.focus(), 0);
    };

    const activateAndFocusObjectives = (index) => {
      setActiveIndex(index);
      setTimeout(() => inputRefs.current[`q-${index}`]?.focus(), 0);
    };

    // ----> DND Library <---- //
    const onDragEnd = (result) => {
      if (!result.destination) return;
      const src = result.source.index;
      const dest = result.destination.index;
      if (src === dest) return;

      setQuestions((prev) => {
        const items = Array.from(prev);
        const [moved] = items.splice(src, 1);
        items.splice(dest, 0, moved);
        return items;
      });

      // Adjust 'activeIndex' so that it continues to point to the same question
      setActiveIndex((prevIdx) => {
        if (prevIdx === null) return null;
        if (prevIdx === src) {
          return dest;
        }
        if (src < dest && prevIdx > src && prevIdx <= dest) {
          return prevIdx - 1;
        }
        if (src > dest && prevIdx >= dest && prevIdx < src) {
          return prevIdx + 1;
        }
        return prevIdx;
      });

      // Wait for the rerender to focus the input in its new position
      setTimeout(() => {
        const currentActive = activeIndex;
        const newIdx =
          typeof currentActive === "number"
            ? currentActive === src
              ? dest
              : (() => {
                  if (
                    src < dest &&
                    currentActive > src &&
                    currentActive <= dest
                  )
                    return currentActive - 1;
                  if (
                    src > dest &&
                    currentActive >= dest &&
                    currentActive < src
                  )
                    return currentActive + 1;
                  return currentActive;
                })()
            : null;
        if (newIdx !== null && inputRefs.current[`q-${newIdx}`]) {
          try {
            inputRefs.current[`q-${newIdx}`].focus();
          } catch (e) {}
        }
      }, 0);
    };

    // Action buttons
    const getButtonConfigs = useCallback(
      (idx, q) => [
        {
          key: "delete",
          iconColor: "pink",
          title: t("Interview.delete_question"),
          iconName: "trash_can",
          disabled: isLoading,
          onClick: (e) => {
            e.stopPropagation();
            removeQuestion(idx);
          },
        },
        {
          key: "duplicate",
          iconColor: "primary",
          title: hasEmptyQuestions
            ? t("Interview.complete_empty_question_before_adding_new_one")
            : t("Interview.duplicate_question"),
          iconName: "outline_document_copy",
          disabled: hasEmptyQuestions || isLoading,
          sx: { opacity: hasEmptyQuestions ? 0.45 : 1 },
          onClick: (e) => {
            e.stopPropagation();
            duplicateQuestionAt(idx);
          },
        },
        {
          key: "toggle",
          iconColor: q.showDetails ? "redII" : "secondary",
          title: q.showDetails
            ? t("Interview.remove_objective")
            : t("Interview.add_objective"),
          iconName: q.showDetails
            ? "outline_note_remove"
            : "outline_document_text",
          disabled: isLoading || hasEmptyQuestions,
          onClick: (e) => {
            e.stopPropagation();
            toggleObservaciones(idx);
          },
        },
        {
          key: "add",
          iconColor: "tealII",
          title: hasEmptyQuestions
            ? t("Interview.complete_empty_question_before_adding_new_one")
            : t("Interview.add_question"),
          iconName: "outline_message_add",
          disabled: hasEmptyQuestions || isLoading,
          sx: { opacity: hasEmptyQuestions ? 0.45 : 1 },
          onClick: (e) => {
            e.stopPropagation();
            insertQuestionAt(idx);
          },
        },
      ],
      [
        isLoading,
        hasEmptyQuestions,
        removeQuestion,
        duplicateQuestionAt,
        toggleObservaciones,
        insertQuestionAt,
        t,
      ]
    );

    const buttonConfigsByIndex = useMemo(() => {
      return questions.map((q, idx) => getButtonConfigs(idx, q));
    }, [questions, getButtonConfigs]);

    // ---- Render ---- //
    return (
      <>
        <Box
          sx={{
            px: 1,
            py: 1.25,
            width: "100%",
            maxWidth: 800,
            mx: "auto",
            borderRadius: 1,
            pb: 1.5,
            mb: { md: 1 },
          }}
        >
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="questions-droppable">
              {(dropProvided) => (
                <Stack
                  spacing={1}
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                >
                  {questions.map((quest, index) => (
                    <Draggable
                      key={index}
                      draggableId={`question-${index}`}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <Paper
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...provided.draggableProps.style,
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            boxShadow: snapshot.isDragging
                              ? `0 0 5px 3px ${theme.palette.silver.main}`
                              : "",
                          }}
                          sx={{
                            position: "relative",
                            borderLeft:
                              activeIndex === index &&
                              `6px solid ${theme.palette.primary.light}`,
                            cursor: "pointer",
                            transition: "border-color 0.3s ease",
                            maxWidth: { xs: "100%", md: "93%" },
                            "&:hover .hoverMove": {
                              opacity: 1,
                              visibility: "visible",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveIndex(index);
                          }}
                          elevation={1}
                        >
                          <Box
                            className="hoverMove"
                            {...provided.dragHandleProps}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              m: 0.5,
                              cursor: isMobil ? "default" : "grab",
                              opacity: activeIndex === index ? 1 : 0,
                              visibility:
                                activeIndex === index ? "visible" : "hidden",
                              transition:
                                "opacity 0.3s ease, visibility 0.3s ease",
                              userSelect: "none",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <OutlineMove
                              size={15}
                              color={theme.palette.secondary.light}
                              style={{ transform: "rotate(90deg)" }}
                            />
                          </Box>

                          <Box
                            sx={{ px: { xs: 1, md: 2 }, pb: { xs: 1, md: 2 } }}
                          >
                            {activeIndex === index ? (
                              <TextField
                                id={`q-${index}`}
                                inputRef={(el) =>
                                  (inputRefs.current[`q-${index}`] = el)
                                }
                                label={`${t("Interview.question")} #${
                                  index + 1
                                }`}
                                variant="filled"
                                multiline
                                minRows={1}
                                maxRows={6}
                                fullWidth
                                value={quest.question}
                                onFocus={() => setActiveIndex(index)}
                                disabled={isLoading}
                                onChange={(e) =>
                                  hdlChangeQuestion(
                                    index,
                                    "question",
                                    e.target.value
                                  )
                                }
                                sx={{
                                  "& .MuiInputBase-input": { fontWeight: 600 },
                                  "& .MuiFilledInput-root.Mui-focused": {
                                    backgroundColor:
                                      theme.palette.blueGray50.main,
                                  },
                                  "& .MuiInputLabel-shrink": {
                                    fontSize: "1.05rem",
                                  },
                                }}
                              />
                            ) : (
                              <Stack
                                onClick={() => activateAndFocusQuestion(index)}
                                px={1}
                                sx={{
                                  py: 0.5,
                                  borderLeft: `5px solid ${theme.palette.blueGray100.main}`,
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="textSecondary"
                                >
                                  {`${t("Interview.question")} #${index + 1}`}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  fontWeight={600}
                                  px={0.25}
                                  sx={{ whiteSpace: "pre-line" }}
                                >
                                  {quest?.question || ""}
                                </Typography>
                              </Stack>
                            )}

                            {quest.showDetails && (
                              <>
                                {activeIndex === index ? (
                                  <TextField
                                    label={`${t("Interview.objective")}:`}
                                    inputRef={(el) =>
                                      (inputRefs.current[`d-${index}`] = el)
                                    }
                                    id={`d-${index}`}
                                    placeholder={t(
                                      "Interview.describe_main_objective_of_this_question"
                                    )}
                                    variant="outlined"
                                    multiline
                                    minRows={1}
                                    maxRows={4}
                                    fullWidth
                                    sx={{
                                      mt: 1.5,
                                      "& .MuiInputBase-input": {
                                        fontStyle: "oblique",
                                        fontSize: "0.83rem",
                                        lineHeight: 1.3,
                                      },
                                      "& .MuiInputLabel-root": {
                                        fontSize: "0.75rem",
                                      },
                                    }}
                                    value={quest.details}
                                    onClick={(e) => e.stopPropagation()}
                                    onFocus={() => setActiveIndex(index)}
                                    disabled={isLoading}
                                    onChange={(e) =>
                                      hdlChangeQuestion(
                                        index,
                                        "details",
                                        e.target.value
                                      )
                                    }
                                  />
                                ) : (
                                  <Stack
                                    sx={{ mt: 1, px: 1 }}
                                    onClick={() =>
                                      activateAndFocusObjectives(index)
                                    }
                                    aria-label={`${t("Interview.objective")}: ${
                                      quest?.details || ""
                                    }`}
                                  >
                                    {quest.details.trim() ? (
                                      <>
                                        <Typography
                                          color="textSecondary"
                                          fontSize={12}
                                          fontWeight={600}
                                        >
                                          {`${t("Interview.objective")}:`}
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          fontStyle="oblique"
                                          px={0.5}
                                          sx={{
                                            fontSize: "0.83rem",
                                            whiteSpace: "pre-line",
                                          }}
                                        >
                                          {quest.details}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Box sx={{ height: 5 }} />
                                    )}
                                  </Stack>
                                )}
                              </>
                            )}

                            <Fade in={activeIndex === index} timeout={200}>
                              <Stack
                                direction={isMobil ? "row" : "column"}
                                spacing={isMobil ? 0 : 0.5}
                                sx={{
                                  px: isMobil ? 1 : 0,
                                  pt: isMobil ? 0.5 : 0,
                                  mt: isMobil ? 1.5 : 0,
                                  alignItems: "center",
                                  justifyContent: isMobil
                                    ? "flex-end"
                                    : "center",
                                  flexFlow: isMobil
                                    ? "row nowrap"
                                    : "column nowrap",
                                  borderTop: isMobil
                                    ? `1px solid ${theme.palette.blueGray100.main}`
                                    : undefined,
                                  // Desktop box
                                  position: isMobil ? "static" : "absolute",
                                  top: isMobil ? undefined : 0,
                                  right: isMobil ? undefined : -54,
                                  backgroundColor: isMobil
                                    ? "transparent"
                                    : theme.palette.background.paper,
                                  p: isMobil ? 0 : 0.5,
                                  borderRadius: isMobil ? 0 : 2,
                                  "&::after": isMobil
                                    ? undefined
                                    : {
                                        content: '""',
                                        position: "absolute",
                                        left: -8,
                                        top: 30,
                                        borderTop: "9px solid transparent",
                                        borderBottom: "9px solid transparent",
                                        borderRight: `9px solid ${theme.palette.background.paper}`,
                                        zIndex: 1,
                                      },

                                  zIndex: isMobil ? "auto" : 1200,
                                }}
                              >
                                {/* configs memoized by index(fallback a getButtonConfigs) */}
                                {(() => {
                                  const configs =
                                    buttonConfigsByIndex[index] ??
                                    getButtonConfigs(index, quest);
                                  const renderList = isMobil
                                    ? configs
                                    : [...configs].reverse();
                                  return renderList.map((cfg) => (
                                    <EGIconButton
                                      key={cfg.key}
                                      iconColor={cfg.iconColor}
                                      title={cfg.title}
                                      iconName={cfg.iconName}
                                      disabled={cfg.disabled}
                                      placement="left-start"
                                      iconSize={21}
                                      sx={cfg.sx}
                                      onClick={cfg.onClick}
                                    />
                                  ));
                                })()}
                              </Stack>
                            </Fade>
                          </Box>
                        </Paper>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
        <UnsavedChanges
          open={openCancelDialog}
          onClose={() => setOpenCancelDialog(false)}
          onCancel={() => setOpenCancelDialog(false)}
          onConfirm={() => {
            resetQuestions();
            setOpenCancelDialog(false);
            goHome(); // Leave without save
          }}
        />
      </>
    );
  }
);

EditQnA.displayName = "EditQnA";
